// Auth multi-usuario con PBKDF2 offline + JWT online

import type { User } from '@meeting-copilot/shared';

interface UserProfile {
  username: string;
  userId: string;
  displayName: string;
  role: string;
}

interface AuthCredential {
  username: string;
  derivedKey: string;
  salt: string;
}

export class AuthManager {
  private currentUser: User | null = null;
  private jwtToken: string | null = null;
  private db: IDBDatabase | null = null;

  async init(db: IDBDatabase): Promise<void> {
    this.db = db;
    
    const token = localStorage.getItem('jwt_token');
    const username = localStorage.getItem('current_username');
    
    if (token && username) {
      try {
        const decoded = this.decodeJWT(token);
        if (decoded.exp * 1000 > Date.now()) {
          this.jwtToken = token;
          
          // CRÍTICO: Cargar perfil desde IndexedDB por username
          const profile = await this.loadUserProfile(username);
          if (profile) {
            this.currentUser = {
              id: profile.userId,
              username: profile.username,
              displayName: profile.displayName,
              role: profile.role,
            };
          }
        } else {
          this.clearAuth();
        }
      } catch (error) {
        this.clearAuth();
      }
    }
  }

  private async loadUserProfile(username: string): Promise<UserProfile | null> {
    if (!this.db) return null;
    const tx = this.db.transaction('user_profiles', 'readonly');
    const request = tx.objectStore('user_profiles').get(username);
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  private async saveUserProfile(profile: UserProfile): Promise<void> {
    if (!this.db) return;
    const tx = this.db.transaction('user_profiles', 'readwrite');
    await tx.objectStore('user_profiles').put(profile);
  }

  async login(username: string, pin: string): Promise<boolean> {
    if (navigator.onLine) {
      try {
        const response = await fetch('/api/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, pin }),
        });
        
        if (response.ok) {
          const data = await response.json();
          this.jwtToken = data.token;
          this.currentUser = data.user;
          
          localStorage.setItem('jwt_token', data.token);
          localStorage.setItem('current_username', username);
          
          // CRÍTICO: Guardar perfil y credencial por usuario
          await this.saveUserProfile({
            username: data.user.username,
            userId: data.user.id,
            displayName: data.user.displayName,
            role: data.user.role,
          });
          
          await this.storeOfflineCredential(username, pin);
          
          return true;
        }
        return false;
      } catch (error) {
        return await this.loginOffline(username, pin);
      }
    } else {
      return await this.loginOffline(username, pin);
    }
  }

  private async storeOfflineCredential(username: string, pin: string): Promise<void> {
    if (!this.db) return;
    
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const derivedKey = await this.derivePBKDF2(pin, salt);
    
    const credential: AuthCredential = {
      username,
      derivedKey: Array.from(new Uint8Array(derivedKey)).map(b => b.toString(16).padStart(2, '0')).join(''),
      salt: Array.from(salt).map(b => b.toString(16).padStart(2, '0')).join(''),
    };
    
    const tx = this.db.transaction('auth_credentials', 'readwrite');
    await tx.objectStore('auth_credentials').put(credential);
  }

  private async loginOffline(username: string, pin: string): Promise<boolean> {
    if (!this.db) return false;
    
    const tx = this.db.transaction('auth_credentials', 'readonly');
    const request = tx.objectStore('auth_credentials').get(username);
    const credential = await new Promise<AuthCredential | undefined>((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    if (!credential) {
      return false;
    }
    
    const salt = new Uint8Array(credential.salt.match(/.{1,2}/g)!.map(byte => parseInt(byte, 16)));
    const derivedKey = await this.derivePBKDF2(pin, salt);
    const derivedKeyHex = Array.from(new Uint8Array(derivedKey)).map(b => b.toString(16).padStart(2, '0')).join('');
    
    if (derivedKeyHex === credential.derivedKey) {
      // CRÍTICO: Cargar perfil desde user_profiles (no desde localStorage genérico)
      const profile = await this.loadUserProfile(username);
      if (profile) {
        this.currentUser = {
          id: profile.userId,
          username: profile.username,
          displayName: profile.displayName,
          role: profile.role,
        };
        
        localStorage.setItem('current_username', username);
        
        // CRÍTICO: Mantener último JWT real si existe (no inventar token)
        const token = localStorage.getItem('jwt_token');
        if (token) {
          try {
            this.decodeJWT(token); // Validar que no esté corrupto
            this.jwtToken = token;
          } catch {
            this.jwtToken = null;
          }
        }
        
        return true;
      }
    }
    return false;
  }

  private async derivePBKDF2(pin: string, salt: Uint8Array): Promise<ArrayBuffer> {
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(pin),
      'PBKDF2',
      false,
      ['deriveBits']
    );
    
    // Ensure salt is a proper BufferSource
    const saltArray = new Uint8Array(salt);
    return await crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: saltArray.buffer as ArrayBuffer,
        iterations: 100000,
        hash: 'SHA-256',
      },
      keyMaterial,
      256
    );
  }

  logout(): void {
    this.clearAuth();
  }

  private clearAuth(): void {
    this.jwtToken = null;
    this.currentUser = null;
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('current_username');
  }

  getToken(): string | null {
    return this.jwtToken;
  }

  getUser(): User | null {
    return this.currentUser;
  }

  isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  // CRÍTICO: Decode JWT robusto (base64url → base64)
  private decodeJWT(token: string): any {
    const parts = token.split('.');
    if (parts.length !== 3) throw new Error('Invalid JWT');
    
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4 !== 0) {
      base64 += '=';
    }
    
    const payload = atob(base64);
    return JSON.parse(payload);
  }
}

// Global singleton
export const auth = new AuthManager();

